/**
 * Jurivoxa public homepage — exact recreation of reference design.
 * Manrope headlines · Inter body · JetBrains Mono labels
 * Ivory bg · Deep navy · Gold accent
 * Signup modal wired to real onboarding API.
 */

import { useState, useEffect, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import * as Dialog from '@radix-ui/react-dialog';
import {
  Scales, Briefcase, CalendarBlank, Gavel,
  ShieldCheck, Bell, ArrowRight, CircleNotch, CheckCircle,
  XCircle, Copy, Check, User, Buildings, X, List,
} from '@phosphor-icons/react';
import {
  startSession, confirmPayment, getSessionStatus,
  type WorkspaceType, type OnboardingPlan,
  type SessionStatus, type ProvisioningStep,
} from '@/lib/onboardingApi';

// ─── Tokens ────────────────────────────────────────────────────────────────
const C = {
  ivory:     '#FDFCFB',
  navy:      '#0F172A',
  container: '#131b2e',
  gold:      '#C5A059',
  goldBtn:   '#fed488',
  goldText:  '#785a1a',
  emerald:   '#064E3B',
  border:    '#E2E8F0',
  muted:     '#45464d',
  surface:   '#eff4ff',
};

const F = {
  display: '"Manrope", system-ui, sans-serif',
  body:    '"Inter", system-ui, sans-serif',
  mono:    '"JetBrains Mono", monospace',
};

// ─── Plan config ───────────────────────────────────────────────────────────
const CHAMBERS_PLANS: { id: OnboardingPlan; label: string; price: string; features: string[] }[] = [
  { id: 'starter',      label: 'Starter',      price: '£199/mo', features: ['Up to 3 barristers', 'Clerk management', 'Brief allocation', 'Shared diary', 'Reporting'] },
  { id: 'professional', label: 'Professional', price: '£300/mo', features: ['Up to 7 barristers', 'Advanced analytics', 'Priority support', 'Custom branding', 'API access'] },
  { id: 'enterprise',   label: 'Enterprise',   price: 'Custom',  features: ['Tell us your requirements', 'Dedicated manager', 'SLA guarantee', 'Custom integrations', 'On-premise'] },
];

const STEP_LABELS: Record<string, string> = {
  create_chambers:          'Creating workspace',
  create_admin_user:        'Creating administrator account',
  create_barrister_profile: 'Setting up barrister profile',
  seed_roles:               'Configuring roles & permissions',
  seed_practice_areas:      'Seeding practice areas',
  create_subscription:      'Activating subscription',
  finalize:                 'Finalising workspace',
};

function apiErr(err: unknown) {
  return (err as { response?: { data?: { error?: { message?: string } } } })
    ?.response?.data?.error?.message ?? 'Something went wrong. Please try again.';
}

// ─── Input ─────────────────────────────────────────────────────────────────
const inp = {
  style: { fontFamily: F.body, borderColor: C.border, backgroundColor: '#fafafa' },
  className: 'w-full rounded border px-3 py-2 text-sm text-gray-900 dark:text-gray-200 placeholder-gray-400 focus:border-gray-500 focus:outline-none transition-colors',
};

// ─── Signup form ───────────────────────────────────────────────────────────
/**
 * `confirm` replaces what was a `payment` step.
 *
 * That step asked for a cardholder name, a full card number, an expiry and a CVV, checked them in
 * the browser, threw them away and posted the last four digits to an endpoint that charges
 * nothing — while the FIRST screen of this same form already promised "No credit card during
 * trial". Billing is Future work (CAPABILITY-REGISTER.md) and whether a card is wanted up front is
 * still an open commercial question (docs/BILLING-DECISIONS-NEEDED.html), so there was nothing the
 * card could be used for and nothing it could be checked against.
 *
 * What remains is the same gate, doing the part that was always real: the person confirms what
 * they are creating, and the session is released to provisioning.
 */
type FormStep = 'type' | 'plan' | 'details' | 'confirm' | 'provisioning';

function SignupForm() {
  const navigate = useNavigate();
  const [step, setStep]             = useState<FormStep>('type');
  const [workspaceType, setType]    = useState<WorkspaceType>('chambers');
  const [plan, setPlan]             = useState<OnboardingPlan>('starter');
  const [firstName, setFirstName]   = useState('');
  const [lastName, setLastName]     = useState('');
  const [email, setEmail]           = useState('');
  const [orgName, setOrgName]       = useState('');
  // There is deliberately no card state here. See FormStep above; the guard test
  // `signup-card-safety.guard.test.ts` fails if any of it comes back.
  const [token, setToken]           = useState<string | null>(null);
  const [status, setStatus]         = useState<SessionStatus | null>(null);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');
  const [copied, setCopied]         = useState(false);

  useEffect(() => {
    if (step !== 'provisioning' || !token) return;
    let active = true;
    async function poll() {
      try {
        const s = await getSessionStatus(token!);
        if (!active) return;
        setStatus(s);
        if (s.status !== 'provisioned' && s.status !== 'activated' && s.status !== 'failed')
          setTimeout(poll, 2500);
      } catch { /* ignore */ }
    }
    poll();
    return () => { active = false; };
  }, [step, token]);

  async function handleDetails(e: FormEvent) {
    e.preventDefault(); setError('');
    if (!firstName.trim() || !lastName.trim() || !email.trim()) { setError('Please fill in all required fields.'); return; }
    if (workspaceType === 'chambers' && !orgName.trim()) { setError('Chambers name is required.'); return; }
    setLoading(true);
    try {
      const res = await startSession({ workspaceType, plan, email: email.trim().toLowerCase(), firstName: firstName.trim(), lastName: lastName.trim(), organisationName: workspaceType === 'chambers' ? orgName.trim() : undefined });
      setToken(res.token); setStep('confirm');
    } catch (err) { setError(apiErr(err)); }
    finally { setLoading(false); }
  }

  async function handleConfirm(e: FormEvent) {
    e.preventDefault(); setError('');
    if (!token) { setError('Session expired. Refresh.'); return; }
    setLoading(true);
    try {
      await confirmPayment(token);
      setStep('provisioning');
    } catch (err) { setError(apiErr(err)); }
    finally { setLoading(false); }
  }

  function copyLink() {
    const url = status?.provisioning?.steps?.find(s => s.step === 'finalize')?.message?.split('Activation URL: ')[1];
    if (!url) return;
    void navigator.clipboard.writeText(url);
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  }

  function actToken(): string | null {
    const msg = status?.provisioning?.steps?.find(s => s.step === 'finalize')?.message ?? '';
    return msg.match(/\/activate\/([a-f0-9]{64})/)?.[1] ?? null;
  }

  const label = (txt: string) => <label className="block text-xs text-gray-500 mb-1" style={{ fontFamily: F.body }}>{txt}</label>;
  const back  = (to: FormStep, label: string) => (
    <button type="button" onClick={() => setStep(to)} className="text-xs hover:text-gray-600 dark:hover:text-gray-400 transition-colors" style={{ fontFamily: F.body, color: C.muted }}>← {label}</button>
  );

  if (step === 'type') return (
    <div className="space-y-3">
      <p className="text-sm mb-4" style={{ fontFamily: F.body, color: C.muted }}>How will you use Jurivoxa?</p>
      {[
        { wt: 'solo' as WorkspaceType,     icon: <User className="h-4 w-4" />,      title: 'Single Barrister',  sub: 'Self-employed or direct-access — £69.99/month', onClick: () => { setType('solo'); setPlan('solo'); setStep('details'); } },
        { wt: 'chambers' as WorkspaceType, icon: <Buildings className="h-4 w-4" />, title: 'Chambers',        sub: 'Multiple barristers, clerks, shared administration',   onClick: () => { setType('chambers'); setPlan('starter'); setStep('plan'); } },
      ].map(o => (
        <button key={o.wt} onClick={o.onClick}
          className="group w-full rounded border p-4 text-left hover:border-gray-400 transition-colors"
          style={{ borderColor: C.border }}
        >
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded text-gray-500" style={{ background: C.surface }}>
              {o.icon}
            </div>
            <div>
              <div className="text-sm font-semibold text-gray-900 dark:text-gray-200" style={{ fontFamily: F.body }}>{o.title}</div>
              <div className="text-xs mt-0.5" style={{ fontFamily: F.body, color: C.muted }}>{o.sub}</div>
            </div>
            <ArrowRight className="ml-auto h-4 w-4 text-gray-300 group-hover:text-gray-500" />
          </div>
        </button>
      ))}
      {/*
        "No credit card" is now true — the card step it used to contradict is gone. The "30-day
        free trial" half is NOT: this path creates the workspace with `status: 'active'` and never
        sets `trialEndsAt` (workspace-provisioning.ts), so there is no trial window. Stating a
        duration nothing enforces is the same class of promise the card step was making, so the
        claim is reduced to what is actually true. The trial LENGTH is an open commercial question
        (docs/BILLING-DECISIONS-NEEDED.html Q5) and is not this change's to answer.
      */}
      <p className="text-center text-xs pt-1" style={{ fontFamily: F.body, color: '#9c9990' }}>No credit card required</p>
    </div>
  );

  if (step === 'plan') return (
    <div className="space-y-3">
      <div className="flex items-center gap-3 mb-4">{back('type', 'Back')}<span className="text-sm" style={{ fontFamily: F.body, color: C.muted }}>Choose a plan</span></div>
      {CHAMBERS_PLANS.map(p => (
        <button key={p.id} onClick={() => { setPlan(p.id); setStep('details'); }}
          className="w-full rounded border p-4 text-left transition-colors hover:border-gray-400"
          style={{ borderColor: plan === p.id ? '#374151' : C.border }}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-200" style={{ fontFamily: F.body }}>{p.label}</span>
            <span className="text-sm text-gray-700 dark:text-gray-300" style={{ fontFamily: F.body }}>{p.price}</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {p.features.map(f => (
              <span key={f} className="flex items-center gap-1 text-xs" style={{ fontFamily: F.body, color: C.muted }}>
                <Check className="h-3 w-3 text-gray-500" weight="bold" />{f}
              </span>
            ))}
          </div>
        </button>
      ))}
    </div>
  );

  if (step === 'details') return (
    <form onSubmit={handleDetails} className="space-y-3">
      <div className="flex items-center gap-3 mb-4">{back(workspaceType === 'solo' ? 'type' : 'plan', 'Back')}<span className="text-sm" style={{ fontFamily: F.body, color: C.muted }}>Your details</span></div>
      {workspaceType === 'chambers' && (
        <div>{label('Chambers name')}<input {...inp} type="text" value={orgName} onChange={e => setOrgName(e.target.value)} placeholder="e.g. Smith & Co Chambers" required /></div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <div>{label('First name')}<input {...inp} type="text" value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="James" required /></div>
        <div>{label('Last name')}<input {...inp} type="text" value={lastName} onChange={e => setLastName(e.target.value)} placeholder="Thornton" required /></div>
      </div>
      <div>
        {label('Email address')}
        <input {...inp} type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="j.thornton@chambers.co.uk" required />
        <p className="mt-1 text-xs" style={{ fontFamily: F.body, color: '#9c9990' }}>Activation link will be sent here.</p>
      </div>
      {error && <p className="rounded bg-red-50 px-3 py-2 text-xs text-red-600 dark:text-red-400">{error}</p>}
      <button type="submit" disabled={loading}
        className="flex w-full items-center justify-center gap-2 rounded py-2.5 text-sm font-semibold disabled:opacity-50 transition-all hover:opacity-90"
        style={{ background: C.navy, color: '#fff', fontFamily: F.body }}
      >
        {loading ? <CircleNotch className="h-4 w-4 animate-spin" /> : <>Continue <ArrowRight className="h-4 w-4" /></>}
      </button>
    </form>
  );

  if (step === 'confirm') return (
    <form onSubmit={handleConfirm} className="space-y-3">
      <div className="flex items-center gap-3 mb-4">{back('details', 'Back')}<span className="text-sm" style={{ fontFamily: F.body, color: C.muted }}>Confirm and create</span></div>

      {/*
        What this screen says is limited to what the system actually does. The password signup
        path creates the workspace with full access and takes nothing: `runProvisioning` creates
        the Chambers with `status: 'active'` and never sets `trialEndsAt`, so there is no trial
        clock, no read-only lock and no charge. The previous copy promised "30-day free trial" and
        "You will not be charged until your trial ends" — a trial this path does not create, and a
        charge nothing in the project can make.
      */}
      <div className="rounded border px-3 py-2.5" style={{ borderColor: '#bfdbfe', background: 'var(--surface-selected)' }}>
        <p className="text-xs font-medium text-blue-800 dark:text-blue-200" style={{ fontFamily: F.body }}>No card required</p>
        <p className="text-xs text-blue-600 dark:text-blue-400" style={{ fontFamily: F.body }}>
          We take no payment during signup and collect no card details. Your workspace is created with full access.
        </p>
      </div>

      <div className="rounded border divide-y" style={{ borderColor: C.border }}>
        {[
          { k: 'Workspace', v: workspaceType === 'chambers' ? (orgName.trim() || 'Chambers') : `${firstName.trim()} ${lastName.trim()}`.trim() },
          { k: 'Type',      v: workspaceType === 'chambers' ? 'Chambers' : 'Single barrister' },
          { k: 'Plan',      v: plan.charAt(0).toUpperCase() + plan.slice(1) },
          { k: 'Email',     v: email.trim().toLowerCase() },
        ].map(row => (
          <div key={row.k} className="flex items-baseline justify-between gap-3 px-3 py-2">
            <span className="text-xs shrink-0" style={{ fontFamily: F.body, color: C.muted }}>{row.k}</span>
            <span className="text-xs text-right break-words text-gray-900 dark:text-gray-200" style={{ fontFamily: F.body }}>{row.v}</span>
          </div>
        ))}
      </div>

      {error && <p className="rounded bg-red-50 px-3 py-2 text-xs text-red-600 dark:text-red-400">{error}</p>}
      <button type="submit" disabled={loading}
        className="flex w-full items-center justify-center gap-2 rounded py-2.5 text-sm font-semibold disabled:opacity-50 transition-all hover:opacity-90"
        style={{ background: C.navy, color: '#fff', fontFamily: F.body }}
      >
        {loading ? <CircleNotch className="h-4 w-4 animate-spin" /> : <>Create my workspace <ArrowRight className="h-4 w-4" /></>}
      </button>
      <p className="text-center text-xs" style={{ fontFamily: F.body, color: '#9c9990' }}>
        We'll email {email.trim().toLowerCase() || 'you'} a link to set your password.
      </p>
    </form>
  );

  const isDone   = status?.status === 'provisioned' || status?.status === 'activated';
  const isFailed = status?.status === 'failed';
  const steps    = (status?.provisioning?.steps ?? []) as ProvisioningStep[];
  const at       = actToken();

  return (
    <div className="space-y-4">
      {!isDone && !isFailed && (
        <div className="flex items-center gap-3">
          <CircleNotch className="h-4 w-4 animate-spin" style={{ color: C.navy }} />
          <div><p className="text-sm font-medium text-gray-900 dark:text-gray-200" style={{ fontFamily: F.body }}>Setting up your workspace</p><p className="text-xs mt-0.5" style={{ fontFamily: F.body, color: C.muted }}>About 10 seconds.</p></div>
        </div>
      )}
      {isDone && (
        <div className="flex items-center gap-3">
          <CheckCircle className="h-4 w-4 text-emerald-600 dark:text-emerald-400" weight="fill" />
          <div><p className="text-sm font-medium text-gray-900 dark:text-gray-200" style={{ fontFamily: F.body }}>Workspace ready</p><p className="text-xs mt-0.5" style={{ fontFamily: F.body, color: C.muted }}>Set your password to sign in.</p></div>
        </div>
      )}
      {isFailed && (
        <div className="flex items-center gap-3">
          <XCircle className="h-4 w-4 text-red-500" weight="fill" />
          <div><p className="text-sm font-medium text-gray-900 dark:text-gray-200" style={{ fontFamily: F.body }}>Provisioning failed</p><p className="text-xs mt-0.5 text-red-500" style={{ fontFamily: F.body }}>{status?.provisioning?.errorMessage ?? 'Contact support@jurivoxa.com'}</p></div>
        </div>
      )}
      {steps.length > 0 && (
        <div className="rounded border bg-gray-50 p-3 space-y-2" style={{ borderColor: C.border }}>
          {steps.map(s => (
            <div key={s.step} className="flex items-center gap-2">
              {s.status === 'ok' ? <CheckCircle className="h-3.5 w-3.5 flex-shrink-0 text-emerald-600 dark:text-emerald-400" weight="fill" /> : <XCircle className="h-3.5 w-3.5 flex-shrink-0 text-red-400" weight="fill" />}
              <span className="text-xs text-gray-600 dark:text-gray-400" style={{ fontFamily: F.body }}>{STEP_LABELS[s.step] ?? s.step}</span>
            </div>
          ))}
        </div>
      )}
      {isDone && at && (
        <div className="space-y-2">
          <button onClick={() => navigate(`/activate/${at}`)}
            className="flex w-full items-center justify-center gap-2 rounded py-2.5 text-sm font-semibold hover:opacity-90 transition-all"
            style={{ background: C.navy, color: '#fff', fontFamily: F.body }}
          >
            Set your password & sign in <ArrowRight className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-2 rounded border bg-gray-50 px-3 py-1.5" style={{ borderColor: C.border }}>
            <span className="flex-1 truncate text-xs font-mono text-gray-500">{window.location.origin}/activate/{at}</span>
            <button onClick={copyLink} className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800 dark:hover:text-gray-200 transition-colors">
              {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}{copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <p className="text-center text-xs" style={{ fontFamily: F.body, color: '#9c9990' }}>Link valid for 7 days.</p>
        </div>
      )}
    </div>
  );
}

// ─── Signup modal ──────────────────────────────────────────────────────────
function SignupModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Dialog.Root open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border bg-white p-6 shadow-xl focus:outline-none"
          style={{ borderColor: C.border }}
          aria-describedby={undefined}
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2.5">
              <div className="flex h-7 w-7 items-center justify-center rounded" style={{ background: C.navy }}>
                <Scales className="h-3.5 w-3.5 text-white" weight="duotone" />
              </div>
              <span className="text-sm font-semibold text-gray-900 dark:text-gray-200" style={{ fontFamily: F.body }}>Start your free trial</span>
            </div>
            <Dialog.Close asChild>
              <button className="text-gray-300 hover:text-gray-600 dark:hover:text-gray-400 transition-colors"><X className="h-4 w-4" /></button>
            </Dialog.Close>
          </div>
          <SignupForm />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ─── Bento card ────────────────────────────────────────────────────────────
function BentoCard({ children, className = '', style = {} }: { children: React.ReactNode; className?: string; style?: React.CSSProperties }) {
  return (
    <div
      className={`rounded-xl border p-8 flex flex-col justify-between transition-all duration-300 hover:-translate-y-1 ${className}`}
      style={{ borderColor: C.border, ...style }}
    >
      {children}
    </div>
  );
}

// ─── Home page ─────────────────────────────────────────────────────────────
export default function HomePage() {
  const [signupOpen, setSignupOpen] = useState(false);
  // The section links are hidden below lg; without this they simply vanished on a phone and a
  // tablet, leaving no way to reach Solutions / Features / Practices / About.
  // lg, not md: the bar needs ~780px for logo + four links + Client Login + Request Demo, so at
  // md (768px) the links appeared with nowhere to go and overlapped the Client Login button.
  const [navOpen, setNavOpen] = useState(false);

  return (
    <div style={{ background: C.ivory, fontFamily: F.body, color: C.navy }}>
      <SignupModal open={signupOpen} onClose={() => setSignupOpen(false)} />

      {/* ── Navbar ── */}
      <header className="sticky top-0 z-40 border-b" style={{ background: `${C.ivory}f5`, backdropFilter: 'blur(8px)', borderColor: C.border }}>
        <nav className="flex items-center justify-between mx-auto px-5 sm:px-8 lg:px-20 h-16 sm:h-20" style={{ maxWidth: 1280 }}>
          <div className="flex min-w-0 items-center gap-6 lg:gap-12">
            <a href="#" className="flex shrink-0 items-center gap-2">
              <span
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg sm:h-9 sm:w-9"
                style={{ background: C.navy }}
              >
                <Scales className="h-[18px] w-[18px]" style={{ color: C.gold }} weight="bold" />
              </span>
              <span className="font-bold tracking-tight" style={{ fontFamily: F.display, fontSize: 20, color: C.navy }}>Jurivoxa</span>
            </a>
            <div className="hidden lg:flex gap-8">
              {[['Solutions', '#solutions'], ['Features', '#features'], ['Practices', '#practices'], ['About', '#about']].map(([l, h], i) => (
                <a key={l} href={h}
                  className="text-sm transition-colors hover:opacity-80"
                  style={{ fontFamily: F.body, color: i === 0 ? C.navy : C.muted, fontWeight: i === 0 ? 600 : 400, borderBottom: i === 0 ? `2px solid ${C.gold}` : 'none', paddingBottom: i === 0 ? 2 : 0 }}
                >{l}</a>
              ))}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-3 sm:gap-6">
            <Link
              to="/login"
              className="whitespace-nowrap rounded-lg border px-2.5 py-2 text-xs font-semibold transition-colors hover:opacity-70 sm:px-4 sm:py-2.5 sm:text-sm"
              style={{ fontFamily: F.body, color: C.navy, borderColor: C.navy }}
            >Client Login</Link>
            <button
              type="button"
              onClick={() => setNavOpen((v) => !v)}
              aria-label={navOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={navOpen}
              className="flex h-9 w-9 items-center justify-center rounded-lg border transition-colors lg:hidden"
              style={{ borderColor: C.border, color: C.navy }}
            >
              {navOpen ? <X className="h-5 w-5" /> : <List className="h-5 w-5" />}
            </button>
            <button
              onClick={() => setSignupOpen(true)}
              className="hidden whitespace-nowrap rounded-lg px-3 py-2 text-xs font-semibold transition-all hover:opacity-80 active:scale-95 sm:inline-block sm:px-6 sm:py-3 sm:text-sm"
              style={{ background: C.navy, color: '#fff', fontFamily: F.body }}
            >Request Demo</button>
          </div>
        </nav>

        {/* Mobile section menu — the same four links the desktop bar shows */}
        {navOpen && (
          <div className="border-t lg:hidden" style={{ borderColor: C.border, background: C.ivory }}>
            <div className="mx-auto flex flex-col px-5 py-2 sm:px-8" style={{ maxWidth: 1280 }}>
              {[['Solutions', '#solutions'], ['Features', '#features'], ['Practices', '#practices'], ['About', '#about']].map(([l, h]) => (
                <a
                  key={l}
                  href={h}
                  onClick={() => setNavOpen(false)}
                  className="border-b py-3 text-sm last:border-0"
                  style={{ fontFamily: F.body, color: C.navy, borderColor: C.border }}
                >{l}</a>
              ))}
              <button
                type="button"
                onClick={() => { setNavOpen(false); setSignupOpen(true); }}
                className="my-3 rounded-lg px-4 py-3 text-sm font-semibold sm:hidden"
                style={{ background: C.navy, color: '#fff', fontFamily: F.body }}
              >Request Demo</button>
            </div>
          </div>
        )}
      </header>

      <main>

        {/* ── Hero ── */}
        <section
          className="relative overflow-hidden pb-16 pt-10 sm:pb-24 sm:pt-16 lg:pb-32 lg:pt-24"
          style={{ background: `radial-gradient(circle at 70% 30%, rgba(197,160,89,0.05) 0%, transparent 70%)` }}
        >
          <div className="mx-auto grid items-center gap-10 px-5 sm:px-8 lg:gap-16 lg:px-20 md:grid-cols-2" style={{ maxWidth: 1280 }}>
            {/* Left */}
            <div className="z-10">
              <span className="mb-4 block text-xs font-medium tracking-[0.12em] lg:mb-6" style={{ fontFamily: F.mono, color: C.gold, textTransform: 'uppercase' }}>
                Legal Operating System
              </span>
              <h1 className="mb-5 leading-tight lg:mb-8" style={{ fontFamily: F.display, fontSize: 'clamp(40px, 5vw, 64px)', fontWeight: 700, color: C.navy }}>
                The System of Action for{' '}
                <span style={{ color: C.gold, fontStyle: 'italic' }}>Modern</span>{' '}
                Barristers.
              </h1>
              <p className="mb-7 max-w-lg text-base leading-relaxed lg:mb-10" style={{ fontFamily: F.body, color: C.muted }}>
                Move legal work forward with unified intelligence. Jurivoxa integrates matter management, hearing coordination, clerk workflows, and BSB compliance into a single authoritative platform.
              </p>
              <div className="flex flex-wrap gap-4">
                <button
                  onClick={() => setSignupOpen(true)}
                  className="rounded-lg px-8 py-4 font-bold hover:brightness-95 transition-all"
                  style={{ background: '#fed488', color: '#785a1a', fontFamily: F.body }}
                >Start Free Trial</button>
                <button
                  onClick={() => setSignupOpen(true)}
                  className="rounded-lg px-8 py-4 font-bold transition-all hover:text-white"
                  style={{ border: `1px solid ${C.navy}`, color: C.navy, fontFamily: F.body, background: 'transparent' }}
                  onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = C.navy; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; (e.currentTarget as HTMLButtonElement).style.color = C.navy; }}
                >Book a Demo</button>
              </div>
            </div>

            {/* Right — brand panel.
                NOT a screenshot and not a mock of the product: an emblem in the house palette.
                Drop a real photograph in here when one exists — replace the inner block with an
                <img> and keep the frame, the ratio and the shadow as they are. */}
            <div className="relative">
              <div
                className="relative flex aspect-[4/3] items-center justify-center overflow-hidden rounded-xl shadow-2xl"
                style={{ background: `linear-gradient(145deg, ${C.navy} 0%, ${C.container} 100%)`, border: `1px solid ${C.border}` }}
              >
                {/* Concentric rings, echoing the closing section */}
                <div className="pointer-events-none absolute inset-0 opacity-[0.18]">
                  <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full" style={{ width: '120%', paddingBottom: '120%', border: `1px solid ${C.gold}` }} />
                  <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full" style={{ width: '85%', paddingBottom: '85%', border: `1px solid ${C.gold}` }} />
                  <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full" style={{ width: '50%', paddingBottom: '50%', border: `1px solid ${C.gold}` }} />
                </div>

                <div className="relative flex flex-col items-center gap-4 px-6 text-center">
                  <span
                    className="flex h-16 w-16 items-center justify-center rounded-2xl sm:h-20 sm:w-20"
                    style={{ background: 'rgba(197,160,89,0.12)', border: `1px solid rgba(197,160,89,0.35)` }}
                  >
                    <Scales className="h-8 w-8 sm:h-10 sm:w-10" style={{ color: C.gold }} weight="bold" />
                  </span>
                  <div>
                    <div className="text-xl font-bold sm:text-2xl" style={{ fontFamily: F.display, color: C.ivory }}>Jurivoxa</div>
                    <div className="mt-1 text-[11px] uppercase tracking-[0.18em]" style={{ fontFamily: F.mono, color: C.gold }}>Built for the Bar</div>
                  </div>
                </div>
              </div>
              {/* Decorative blur */}
              <div className="absolute -bottom-6 -left-6 -z-10 rounded-full blur-2xl" style={{ width: 128, height: 128, background: 'rgba(197,160,89,0.1)' }} />
            </div>
          </div>
        </section>

        {/* ── Capability strip (RC-10: honest product statement — removed fabricated years / G2
             rating / partner logos; only claims we can demonstrate) ── */}
        <section className="border-y py-10" style={{ background: '#fff', borderColor: C.border }}>
          <div className="mx-auto px-5 sm:px-8 lg:px-20 flex flex-wrap justify-center items-center gap-x-10 gap-y-3 text-center" style={{ maxWidth: 1280 }}>
            {['Matters & hearings', 'Barrister diary', 'Fees & billing', 'Clerk coordination', 'BSB-aware workflows'].map((label, i, arr) => (
              <span key={label} className="flex items-center gap-x-10">
                <span className="text-sm font-semibold" style={{ fontFamily: F.body, color: C.navy }}>{label}</span>
                {i < arr.length - 1 && <span className="hidden lg:inline h-4 w-px" style={{ background: C.border }} />}
              </span>
            ))}
          </div>
        </section>

        {/* ── Features bento ── */}
        <section id="features" className="py-28">
          <div className="mx-auto px-5 sm:px-8 lg:px-20" style={{ maxWidth: 1280 }}>
            {/* Section header */}
            <div className="mb-16 text-center">
              <h2 className="mb-4" style={{ fontFamily: F.display, fontSize: 'clamp(28px,4vw,42px)', fontWeight: 700, color: C.navy }}>
                Every part of your day,{' '}
                <br />
                <span style={{ color: C.gold, fontStyle: 'italic' }}>managed with precision</span>
              </h2>
              <p className="max-w-2xl mx-auto text-base" style={{ fontFamily: F.body, color: C.muted }}>
                Jurivoxa automates the routine so you can focus on the work that matters. Built specifically for barristers — not adapted from solicitor software.
              </p>
            </div>

            {/* Bento grid */}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6">

              {/* Large card — Matter Management */}
              <BentoCard className="md:col-span-8 bg-white" style={{ borderColor: C.border }}>
                <div>
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center text-white mb-6" style={{ background: C.emerald }}>
                    <Briefcase className="h-5 w-5" />
                  </div>
                  <h3 className="mb-4" style={{ fontFamily: F.display, fontSize: 24, fontWeight: 600, color: C.navy }}>Matter Management</h3>
                  <p className="max-w-md" style={{ fontFamily: F.body, color: C.muted, lineHeight: 1.6 }}>
                    Every brief from intake to outcome. Hearings, fees, documents, tasks and communications — all linked to one matter record with a full audit trail throughout.
                  </p>
                </div>
                {/* Mock UI */}
                <div className="mt-8 rounded-lg border p-6 overflow-hidden" style={{ background: C.ivory, borderColor: C.border }}>
                  <div className="flex flex-col gap-3">
                    <div className="h-3 w-3/4 rounded animate-pulse" style={{ background: C.border }} />
                    <div className="h-3 w-1/2 rounded animate-pulse opacity-60" style={{ background: C.border }} />
                    <div className="h-3 w-5/6 rounded animate-pulse opacity-40" style={{ background: C.border }} />
                  </div>
                  <div className="mt-4 flex justify-between items-center">
                    <span className="text-xs font-medium" style={{ fontFamily: F.mono, color: C.emerald, letterSpacing: '0.08em' }}>MATTERS SYNCED</span>
                    <span className="text-xs" style={{ fontFamily: F.body, color: C.muted }}>Audit logged</span>
                  </div>
                </div>
              </BentoCard>

              {/* Dark card — Brief Allocation */}
              <BentoCard
                className="md:col-span-4 text-white"
                style={{ background: C.container, borderColor: C.container }}
              >
                <div>
                  <span className="block mb-4 text-[10px] font-medium tracking-widest" style={{ fontFamily: F.mono, color: C.gold }}>ALLOCATION</span>
                  <h3 className="mb-4 text-white" style={{ fontFamily: F.display, fontSize: 20, fontWeight: 600 }}>Brief Intake &amp; CRM</h3>
                  <p className="text-sm leading-relaxed" style={{ fontFamily: F.body, color: '#7c839b' }}>
                    Seamless brief intake that converts enquiries into allocated matters without manual entry. One offer per case enforced.
                  </p>
                </div>
                <div className="flex justify-end">
                  <Gavel className="h-10 w-10 opacity-20 text-white" />
                </div>
              </BentoCard>

              {/* Fees */}
              <BentoCard className="md:col-span-4 bg-white" style={{ borderColor: C.border }}>
                <div>
                  <h3 className="mb-4" style={{ fontFamily: F.display, fontSize: 20, fontWeight: 600, color: C.navy }}>Fees &amp; Billing</h3>
                  <p className="text-sm leading-relaxed" style={{ fontFamily: F.body, color: C.muted }}>Fee records, PDF generation, payment tracking, and automatic notifications on receipt.</p>
                </div>
                <div className="mt-6 flex items-end gap-2">
                  <span className="font-bold" style={{ fontFamily: F.display, fontSize: 28, color: C.navy }}>£124,500</span>
                  <span className="text-xs font-bold mb-1" style={{ fontFamily: F.body, color: C.emerald }}>↑ 12%</span>
                </div>
              </BentoCard>

              {/* Compliance */}
              <BentoCard className="md:col-span-4" style={{ background: C.ivory, borderColor: C.border }}>
                <div>
                  <h3 className="mb-4" style={{ fontFamily: F.display, fontSize: 20, fontWeight: 600, color: C.navy }}>BSB Compliance</h3>
                  <p className="text-sm leading-relaxed" style={{ fontFamily: F.body, color: C.muted }}>Conflict checks at intake, full audit log on every action, cab-rank enforcement, and role-based access controls.</p>
                </div>
                <div className="flex gap-2 mt-4">
                  <div className="px-2 py-1 rounded text-[10px] font-medium text-white" style={{ background: C.navy, fontFamily: F.mono, letterSpacing: '0.06em' }}>SECURED</div>
                  <div className="px-2 py-1 rounded text-[10px] font-medium text-white" style={{ background: C.navy, fontFamily: F.mono, letterSpacing: '0.06em' }}>AUDITED</div>
                </div>
              </BentoCard>

              {/* Diary */}
              <BentoCard className="md:col-span-4 bg-white" style={{ borderColor: C.border }}>
                <div>
                  <h3 className="mb-4" style={{ fontFamily: F.display, fontSize: 20, fontWeight: 600, color: C.navy }}>Diary &amp; Hearings</h3>
                  <p className="text-sm leading-relaxed" style={{ fontFamily: F.body, color: C.muted }}>Court list, conference scheduling, and clerk-managed availability — real-time and conflict-checked.</p>
                </div>
                <div className="mt-4 flex items-center justify-between" style={{ color: C.muted }}>
                  <CalendarBlank className="h-5 w-5" />
                  <ArrowRight className="h-4 w-4" />
                </div>
              </BentoCard>

            </div>
          </div>
        </section>

        {/* ── CTA ── */}
        <section className="py-28 relative overflow-hidden" style={{ background: C.container }}>
          <div className="mx-auto px-5 sm:px-8 lg:px-20 relative z-10 text-center" style={{ maxWidth: 1280 }}>
            <h2 className="mb-8 text-white" style={{ fontFamily: F.display, fontSize: 'clamp(28px,4vw,48px)', fontWeight: 700 }}>
              Ready to modernise your practice?
            </h2>
            <p className="max-w-2xl mx-auto mb-12 text-base" style={{ fontFamily: F.body, color: '#7c839b', lineHeight: 1.7 }}>
              Join barristers across England &amp; Wales using Jurivoxa to run leaner, fully compliant, and better-organised chambers. No card required to start.
            </p>
            <div className="flex flex-col justify-center gap-3 sm:flex-row sm:gap-6">
              <button
                onClick={() => setSignupOpen(true)}
                className="whitespace-nowrap rounded-lg px-6 py-4 text-base font-bold transition-all hover:brightness-110 sm:px-10 sm:py-5 sm:text-lg"
                style={{ background: C.gold, color: C.navy, fontFamily: F.body }}
              >Start Your Free Trial</button>
              <button
                className="whitespace-nowrap rounded-lg px-6 py-4 text-base font-bold text-white transition-all hover:bg-white/10 sm:px-10 sm:py-5 sm:text-lg"
                style={{ border: '1px solid #3f465c', fontFamily: F.body }}
              >Schedule Consultation</button>
            </div>
          </div>
          {/* Decorative rings */}
          <div className="absolute top-0 right-0 w-1/2 h-full opacity-10 pointer-events-none">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full" style={{ width: 800, height: 800, border: `1px solid ${C.gold}`, animation: 'spin 60s linear infinite' }} />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full" style={{ width: 600, height: 600, border: `1px solid ${C.gold}`, animation: 'spin 45s linear infinite reverse' }} />
          </div>
        </section>

      </main>

      {/* ── Footer ── */}
      <footer className="hidden border-t md:block" style={{ background: C.container, borderColor: '#3f465c' }}>
        <div className="mx-auto px-5 py-16 sm:px-8 lg:px-20 lg:py-28" style={{ maxWidth: 1280 }}>
          <div className="mb-8 flex flex-col gap-5 md:mb-12 md:grid md:grid-cols-4 md:gap-8 lg:mb-20 lg:gap-12">
            {/* Brand */}
            <div className="md:col-span-1">
              <a href="#" className="mb-2 block text-[22px] font-bold md:mb-4 md:text-[28px] lg:mb-6" style={{ fontFamily: F.display, color: C.ivory }}>Jurivoxa</a>
              <p className="text-sm leading-relaxed opacity-70" style={{ fontFamily: F.body, color: '#7c839b' }}>
                The operating system for modern barristers. Practice management built for the Bar, not adapted from solicitor software.
              </p>
            </div>
            {/* Product */}
            <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 md:block">
              <h4 className="shrink-0 text-sm font-bold text-white md:mb-4 md:text-base lg:mb-6" style={{ fontFamily: F.body }}>Product</h4>
              <ul className="flex flex-wrap gap-x-5 gap-y-2 md:flex-col md:gap-3 lg:gap-4">
                {['Solutions', 'Pricing', 'Security'].map(l => (
                  <li key={l}><a href="#" className="text-sm transition-colors hover:opacity-100" style={{ fontFamily: F.body, color: '#7c839b', opacity: 0.7 }}
                    onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.color = C.gold; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.color = '#7c839b'; }}
                  >{l}</a></li>
                ))}
              </ul>
            </div>
            {/* Company */}
            <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 md:block">
              <h4 className="shrink-0 text-sm font-bold text-white md:mb-4 md:text-base lg:mb-6" style={{ fontFamily: F.body }}>Company</h4>
              <ul className="flex flex-wrap gap-x-5 gap-y-2 md:flex-col md:gap-3 lg:gap-4">
                {['About Us', 'Careers', 'Contact'].map(l => (
                  <li key={l}><a href="#" className="text-sm" style={{ fontFamily: F.body, color: '#7c839b' }}
                    onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.color = C.gold; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.color = '#7c839b'; }}
                  >{l}</a></li>
                ))}
              </ul>
            </div>
            {/* Connect */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 md:block">
              <h4 className="shrink-0 text-sm font-bold text-white md:mb-4 md:text-base lg:mb-6" style={{ fontFamily: F.body }}>Connect</h4>
              <div className="flex gap-3 md:mb-3 lg:mb-6">
                {[ShieldCheck, Bell].map((Icon, i) => (
                  <a key={i} href="#"
                    className="w-10 h-10 rounded-full flex items-center justify-center transition-all"
                    style={{ border: '1px solid #3f465c', color: '#7c839b' }}
                    onMouseEnter={e => { const el = e.currentTarget as HTMLAnchorElement; el.style.borderColor = C.gold; el.style.color = C.gold; }}
                    onMouseLeave={e => { const el = e.currentTarget as HTMLAnchorElement; el.style.borderColor = '#3f465c'; el.style.color = '#7c839b'; }}
                  >
                    <Icon className="h-4 w-4" />
                  </a>
                ))}
              </div>
              <span className="text-xs leading-relaxed" style={{ fontFamily: F.body, color: '#7c839b' }}>
                Security advisories and product updates.
              </span>
            </div>
          </div>
          {/* Bottom bar */}
          <div className="flex flex-col justify-between gap-3 border-t pt-6 md:flex-row md:items-center lg:gap-6 lg:pt-12" style={{ borderColor: '#3f465c' }}>
            <span className="text-xs opacity-70" style={{ fontFamily: F.body, color: '#7c839b' }}>© {new Date().getFullYear()} Jurivoxa Legal Intelligence. All rights reserved.</span>
            <div className="flex flex-wrap gap-x-4 gap-y-1.5 md:justify-end lg:gap-8">
              {['Privacy Policy', 'Terms of Service', 'Security', 'Cookie Settings'].map(l => (
                <a key={l} href="#" className="whitespace-nowrap text-xs opacity-70 transition-colors hover:opacity-100"
                  style={{ fontFamily: F.body, color: '#7c839b' }}
                  onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.color = C.gold; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.color = '#7c839b'; }}
                >{l}</a>
              ))}
            </div>
          </div>
        </div>
      </footer>

      {/* Mobile legal strip — stands in for the hidden footer. Kept deliberately small, but it
          keeps Privacy, Terms and Cookie Settings one tap away, which the law expects of them. */}
      <div className="border-t px-5 py-4 md:hidden" style={{ background: C.container, borderColor: '#3f465c' }}>
        <p className="text-center text-xs opacity-60" style={{ fontFamily: F.body, color: '#7c839b' }}>
          © {new Date().getFullYear()} Jurivoxa. All rights reserved.
        </p>
      </div>

      {/* Spin keyframes */}
      <style>{`@keyframes spin { from { transform: translate(-50%,-50%) rotate(0deg); } to { transform: translate(-50%,-50%) rotate(360deg); } }`}</style>
    </div>
  );
}
